import {
  LibraryBig,
  History,
  Home,
  MessageCircleCheck,
  Bot,
  CalendarCheck,
  Settings,
  Map,
  ChevronDown,
} from 'lucide-react'
import { Link, useRouterState } from '@tanstack/react-router'
import { useState } from 'react'
import type { ReactNode } from 'react'
import { Dropdown } from 'antd'
import { getRoutePermissionRule } from '@/constants/route-permissions'
import { usePermission } from '@/hooks/use-permission'

interface DropdownGroup {
  groupLabel?: string
  items: { label: string; path: string }[]
}

interface MenuItem {
  label: string
  icon: ReactNode
  path?: string
  dropdownGroups?: DropdownGroup[]
  dropdownPlacement?: 'bottom' | 'bottomLeft' | 'bottomRight'
}

export interface MainMenuProps {
  mode?: 'horizontal' | 'vertical'
  onItemClick?: () => void
}

const MENU_ITEMS: MenuItem[] = [
  { label: 'Trang chủ', icon: <Home size={18} />, path: '/dashboards' },
  { label: 'Sự kiện', icon: <CalendarCheck size={18} />, path: '/events' },
  { label: 'Thư viện', icon: <LibraryBig size={18} />, path: '/libraries' },
  {
    label: 'Sơ đồ trung tâm',
    icon: <Map size={18} />,
    path: '/floorplans',
  },
  {
    label: 'Robot',
    icon: <Bot size={18} />,
    path: '/robots',
    // dropdownGroups: [
    //   {
    //     items: [
    //       { label: 'Quản lý robot', path: '/robots' },
    //       { label: 'Lịch sử hoạt động', path: '/activity' },
    //     ],
    //   },
    // ],
  },
  { label: 'Lịch sử tiếp đón', icon: <History size={18} />, path: '/history' },
  {
    label: 'Đánh giá',
    icon: <MessageCircleCheck size={18} />,
    path: '/feedbacks',
  },
  {
    label: 'Cấu hình',
    icon: <Settings size={18} />,
    dropdownPlacement: 'bottomRight',
    dropdownGroups: [
      {
        groupLabel: 'Người dùng:',
        items: [
          { label: 'Tài khoản', path: '/accounts' },
          { label: 'Phân quyền', path: '/permissions' },
        ],
      },
      {
        groupLabel: 'Danh mục:',
        items: [
          { label: 'Đơn vị', path: '/units' },
          { label: 'Quầy', path: '/counters' },
        ],
      },
      {
        groupLabel: 'Cập nhật:',
        items: [
          { label: 'Phiên bản', path: '/versions' },
          { label: 'Cấu hình mail', path: '/mails' },
          { label: 'Mẫu mail', path: '/templates' },
        ],
      },
    ],
  },
]

function NavItem({
  label,
  path,
  pathname,
  getNavItemClasses,
  onItemClick,
}: {
  label: string
  path: string
  pathname: string
  getNavItemClasses: (isActive: boolean) => string
  onItemClick?: () => void
}) {
  const rule = getRoutePermissionRule(path)
  const allowed = usePermission(
    rule ? (rule.kind === 'single' ? rule.permission : rule.permissions) : [],
  )

  if (!allowed) return null

  const isSubActive = pathname.startsWith(path)

  return (
    <Link
      key={path}
      to={path}
      className={getNavItemClasses(isSubActive)}
      onClick={onItemClick}
    >
      {label}
    </Link>
  )
}

function NavGroupColumn({
  group,
  pathname,
  mode,
  getNavItemClasses,
  onItemClick,
}: {
  group: DropdownGroup
  pathname: string
  mode: 'horizontal' | 'vertical'
  getNavItemClasses: (isActive: boolean) => string
  onItemClick?: () => void
}) {
  const allowedItems = group.items.map((subItem) => (
    <NavItem
      key={subItem.path}
      label={subItem.label}
      path={subItem.path}
      pathname={pathname}
      getNavItemClasses={getNavItemClasses}
      onItemClick={onItemClick}
    />
  ))

  return (
    <div
      className={
        mode === 'vertical'
          ? 'flex flex-col w-full'
          : 'flex flex-col min-w-[140px]'
      }
    >
      {group.groupLabel && (
        <div className="text-[12px] text-[#C7C8CA] mb-1 pl-3 font-semibold">
          {group.groupLabel}
        </div>
      )}
      <div className="flex flex-col gap-1">{allowedItems}</div>
    </div>
  )
}

function NavDropdownGroup({
  group,
  groupKey,
  pathname,
  mode,
  getNavItemClasses,
  onItemClick,
}: {
  group: DropdownGroup
  groupKey: string
  pathname: string
  mode: 'horizontal' | 'vertical'
  getNavItemClasses: (isActive: boolean) => string
  onItemClick?: () => void
}) {
  const ruleInputs = group.items.map((subItem) => {
    const rule = getRoutePermissionRule(subItem.path)
    return rule
      ? rule.kind === 'single'
        ? rule.permission
        : rule.permissions
      : []
  })
  const hasAny = usePermission(ruleInputs.flat())

  if (!hasAny) return null

  return (
    <NavGroupColumn
      key={groupKey}
      group={group}
      pathname={pathname}
      mode={mode}
      getNavItemClasses={getNavItemClasses}
      onItemClick={onItemClick}
    />
  )
}

function NavDropdown({
  label,
  icon,
  dropdownGroups,
  pathname,
  isActive,
  mode,
  getNavItemClasses,
  onItemClick,
  dropdownPlacement = 'bottom',
}: {
  label: string
  icon: ReactNode
  dropdownGroups: DropdownGroup[]
  pathname: string
  isActive: boolean
  mode: 'horizontal' | 'vertical'
  getNavItemClasses: (isActive: boolean) => string
  onItemClick?: () => void
  dropdownPlacement?: 'bottom' | 'bottomLeft' | 'bottomRight'
  dropdownOffset?: [number, number]
}) {
  const [expanded, setExpanded] = useState(isActive)

  const groupColumns = dropdownGroups.map((group, index) => (
    <NavDropdownGroup
      key={group.groupLabel || index.toString()}
      groupKey={group.groupLabel || index.toString()}
      group={group}
      pathname={pathname}
      mode={mode}
      getNavItemClasses={getNavItemClasses}
      onItemClick={onItemClick}
    />
  ))

  if (groupColumns.length === 0) return null

  if (mode === 'vertical') {
    return (
      <div className="flex flex-col w-full">
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
          className={`${getNavItemClasses(isActive)} justify-between select-none`}
        >
          <div className="flex items-center gap-3">
            {icon}
            <span>{label}</span>
          </div>
          <ChevronDown
            size={16}
            className={` transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </button>

        {expanded && (
          <div className="flex flex-col gap-1 mt-1">{groupColumns}</div>
        )}
      </div>
    )
  }

  return (
    <Dropdown
      trigger={['hover', 'click']}
      placement={dropdownPlacement}
      popupRender={() => (
        <div className="relative bg-white rounded-2xl shadow-[0_4px_20px_rgba(0,0,0,0.08)] p-3">
          <div className="flex gap-4">{groupColumns}</div>
        </div>
      )}
    >
      <div className={getNavItemClasses(isActive)}>
        {icon}
        <span className="whitespace-nowrap">{label}</span>
      </div>
    </Dropdown>
  )
}

function NavLinkItem({
  item,
  isActive,
  getNavItemClasses,
  onItemClick,
}: {
  item: MenuItem
  isActive: boolean
  getNavItemClasses: (isActive: boolean) => string
  onItemClick?: () => void
}) {
  const rule = getRoutePermissionRule(item.path ?? '')
  const allowed = usePermission(
    rule ? (rule.kind === 'single' ? rule.permission : rule.permissions) : [],
  )

  if (!allowed) return null

  return (
    <Link
      key={item.label}
      to={item.path}
      className={getNavItemClasses(isActive)}
      onClick={onItemClick}
    >
      {item.icon}
      <span className="whitespace-nowrap">{item.label}</span>
    </Link>
  )
}

export function MainMenu({ mode = 'horizontal', onItemClick }: MainMenuProps) {
  const { location } = useRouterState()

  const getNavItemClasses = (isActive: boolean) => {
    const baseClasses =
      mode === 'vertical'
        ? 'flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium no-underline transition-colors cursor-pointer hover:text-[#CE7A58] hover:bg-[rgba(233,122,74,0.12)]'
        : 'inline-flex items-center gap-2 xl:gap-2.5 2xl:gap-3 rounded-full px-2.5 xl:px-3 2xl:px-4 py-2 2xl:py-2.5 text-xs xl:text-sm 2xl:text-base font-medium no-underline transition-colors cursor-pointer whitespace-nowrap shrink-0 hover:text-[#CE7A58] hover:bg-[rgba(233,122,74,0.12)]'

    const activeClasses = 'text-[#CE7A58] bg-[rgba(233,122,74,0.12)]'
    const inactiveClasses = 'text-[#1E2F41] bg-transparent'

    return `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`
  }

  const containerClasses =
    mode === 'vertical'
      ? 'flex flex-col w-full gap-1 bg-transparent border-0 shadow-none'
      : 'rounded-full bg-white p-1 xl:p-1.5 2xl:p-2 flex items-center shadow-[0_1px_2px_rgba(0,0,0,0.04)] w-max shrink-0 max-w-full'

  return (
    <nav aria-label="Điều hướng chính" className={containerClasses}>
      {MENU_ITEMS.map((item) => {
        const isDropdownActive = item.dropdownGroups?.some((group) =>
          group.items.some((child) => location.pathname.startsWith(child.path)),
        )

        const isActive = item.path
          ? location.pathname.startsWith(item.path)
          : !!isDropdownActive

        if (item.dropdownGroups) {
          return (
            <NavDropdown
              key={item.label}
              label={item.label}
              icon={item.icon}
              dropdownGroups={item.dropdownGroups}
              dropdownPlacement={item.dropdownPlacement}
              pathname={location.pathname}
              isActive={isActive}
              mode={mode}
              getNavItemClasses={getNavItemClasses}
              onItemClick={onItemClick}
            />
          )
        }

        return (
          <NavLinkItem
            key={item.label}
            item={item}
            isActive={isActive}
            getNavItemClasses={getNavItemClasses}
            onItemClick={onItemClick}
          />
        )
      })}
    </nav>
  )
}
